
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LucideIcon } from "lucide-react";
import { CSSProperties } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

interface StatsCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  description?: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  className?: string;
  style?: CSSProperties;
  [key: string]: any;
}

export function StatsCard({
  title,
  value,
  icon: Icon,
  description,
  trend,
  className,
  style,
  ...rest 
}: StatsCardProps) {
  const isMobile = useIsMobile();

  return (
    <Card className={cn("overflow-hidden transition-all hover:shadow-hover", className)} style={style} {...rest}>
      <CardHeader className={cn(
        "flex flex-row items-center justify-between space-y-0",
        isMobile ? "px-2 pt-2 pb-0.5" : "px-3 pt-3 pb-1 sm:px-4 sm:pt-4"
      )}>
        <CardTitle className={cn(
          "font-medium text-muted-foreground",
          isMobile ? "text-[10px]" : "text-xs sm:text-sm"
        )}>
          {title}
        </CardTitle>
        <div className={cn(
          "rounded-md bg-primary/10 flex items-center justify-center",
          isMobile ? "w-4 h-4" : "w-6 h-6 sm:w-8 sm:h-8"
        )}>
          <Icon className={cn(
            "text-primary",
            isMobile ? "h-2.5 w-2.5" : "h-4 w-4 sm:h-5 sm:w-5"
          )} />
        </div>
      </CardHeader>
      <CardContent className={cn(
        isMobile ? "px-2 py-1" : "px-3 pb-3 sm:px-4 sm:pb-4"
      )}>
        <div className={cn(
          "font-bold",
          isMobile ? "text-lg" : "text-xl sm:text-2xl"
        )}>
          {value}
        </div>
        {(description || trend) && (
          <div className="flex items-center mt-0.5 sm:mt-1">
            {trend && (
              <span
                className={cn(
                  "mr-1 font-medium",
                  isMobile ? "text-[9px]" : "text-xs",
                  trend.isPositive
                    ? "text-emerald-500"
                    : "text-rose-500"
                )}
              >
                {trend.isPositive ? "+" : ""}
                {trend.value}%
              </span>
            )}
            {description && (
              <span className={cn(
                "text-muted-foreground",
                isMobile ? "text-[8px]" : "text-[10px] sm:text-xs"
              )}>
                {description}
              </span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
